import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';
import { GreenroomState as GS } from '../../../../stores/types/types';
import {
	ConfigurationUl,
	HeaderContainer,
	OpenButton,
	StyledButton,
	StyledLabel,
	StyledLi,
	StyledSelect,
	StyledSmallButton,
	StyledSpan,
	StyledUl,
} from '../debug-ui';
import Icon, { COLORS, ICONS } from '../../../general-components/icon';
import type { LivekitIngress, LivekitIngressType } from '../../../../stores/types/livekit';
import { EntityType } from '../../../../types/enums';
import { CreateLivekitIngress, DeleteLivekitIngress } from '../../../../connection/livekit';
import useDragItem from '../../../../hooks/use-dnd/use-drag-item';
import { DRAG_TYPE } from '../../../../stores/reducers/user-interface';
import { FEED_TYPES } from '../../../../stores/reducers/shots';
import useShots from '../../../../contexts/shots/provider';
import { selectLivekitIngresses } from '../../../../stores/selectors/video';

const INGRESS_TYPES: LivekitIngressType[] = ['whip', 'rtmp', 'srt'];

interface IngressItemProps {
	ingress: LivekitIngress;
	onDelete: (ingressId: string) => Promise<void>;
}

function IngressItem({ ingress, onDelete }: IngressItemProps) {
	const [open, setOpen] = useState<boolean>(true);
	const [isDeleting, setIsDeleting] = useState<boolean>(false);

	const { dragRef, isDragging } = useDragItem(
		{ id: ingress.id, type: DRAG_TYPE.FEED_INGRESS_STREAM },
		true,
	);

	const handleDelete = async () => {
		setIsDeleting(true);
		await onDelete(ingress.id);
		setIsDeleting(false);
	};

	const configFields: [string, string][] = [
		['endpoint', ingress.endpoint],
		...(ingress.streamKey ? [['stream key', ingress.streamKey] as [string, string]] : []),
		['identity', ingress.participantIdentity],
	];

	return (
		<StyledUl ref={dragRef} className={classNames(isDragging && 'dragging')}>
			<OpenButton onClick={() => setOpen(!open)}>
				<Icon
					name={open ? ICONS.KEYBOARD_ARROW_DOWN : ICONS.KEYBOARD_ARROW_UP}
					size={14}
					color={COLORS.WHITE}
				/>
			</OpenButton>
			<StyledLi className="flex width-full padding-right-large">
				<StyledLabel>TYPE:</StyledLabel>
				<StyledSpan>{ingress.type.toUpperCase()}</StyledSpan>
				<StyledLabel className="margin-left-medium">ID:</StyledLabel>
				<StyledSpan className="truncate-overflow">{ingress.id}</StyledSpan>
			</StyledLi>
			{open && (
				<>
					<div className="justify-content-space-between margin-top-small margin-bottom-small">
						<StyledButton
							className="background-green text-dark"
							onClick={() => navigator.clipboard.writeText(ingress.endpoint)}
						>
							Copy Endpoint
						</StyledButton>
						<StyledButton
							className={classNames('background-red text-white', isDeleting && 'disabled')}
							onClick={handleDelete}
							disabled={isDeleting}
						>
							Delete
						</StyledButton>
					</div>
					<StyledLi>
						<ConfigurationUl>
							{configFields.map(([key, value]) => (
								<StyledLi key={key}>
									<StyledLabel>{key}:</StyledLabel>
									<StyledSpan>{value}</StyledSpan>
									<StyledSmallButton
										className="background-green text-dark"
										onClick={() => navigator.clipboard.writeText(value)}
									>
										Copy
									</StyledSmallButton>
								</StyledLi>
							))}
						</ConfigurationUl>
					</StyledLi>
				</>
			)}
		</StyledUl>
	);
}

export default function DebugIngresses() {
	const dispatch = useDispatch();
	const token = useSelector((s: GS) => s.SessionReducer.token);
	const meeting = useSelector((s: GS) => s.SessionReducer.meeting);
	const { standaloneIngresses } = useSelector(selectLivekitIngresses);

	const [open, setOpen] = useState<boolean>(false);
	const [type, setType] = useState<LivekitIngressType>('whip');
	const [isCreating, setIsCreating] = useState<boolean>(false);

	const { stripFeedFromShots } = useShots();

	const handleCreate = async () => {
		if (!token || !meeting) return;
		setIsCreating(true);
		const entity = await CreateLivekitIngress(token, meeting.meeting, type);
		if (entity) dispatch({ type: entity.entityType, payload: entity });
		setIsCreating(false);
	};

	const handleDelete = async (ingressId: string) => {
		if (!token || !meeting) return;
		stripFeedFromShots(ingressId, FEED_TYPES.INGRESS_STREAM);
		await DeleteLivekitIngress(token, meeting.meeting, ingressId);
		dispatch({
			type: `${EntityType.livekitIngress}-delete`,
			payload: { entityValue: { id: ingressId } },
		});
	};

	return (
		<div className="section settings">
			<HeaderContainer>
				<h5>Ingresses</h5>
			</HeaderContainer>
			<div className="advanced-settings-container">
				<StyledUl>
					<OpenButton onClick={() => setOpen(!open)}>
						<Icon
							name={open ? ICONS.KEYBOARD_ARROW_DOWN : ICONS.KEYBOARD_ARROW_UP}
							size={14}
							color={COLORS.WHITE}
						/>
					</OpenButton>
					{open ? (
						<div className="justify-content-space-between margin-top-small margin-bottom-small">
							<div className="flex-align-center">
								<StyledLabel>TYPE:</StyledLabel>
								<StyledSelect
									value={type}
									onChange={(e) => setType(e.target.value as LivekitIngressType)}
									style={{ margin: 0 }}
								>
									{INGRESS_TYPES.map((t) => (
										<option key={t} value={t}>{t.toUpperCase()}</option>
									))}
								</StyledSelect>
							</div>
							<StyledButton
								className="background-green text-dark"
								onClick={handleCreate}
								disabled={isCreating}
							>
								{isCreating ? 'Creating...' : 'Create Ingress'}
							</StyledButton>
						</div>
					) : (
						<div className="flex-center">
							<h5>Create new ingress</h5>
						</div>
					)}
				</StyledUl>
			</div>
			<div className="advanced-settings-container">
				{standaloneIngresses.map((ingress: LivekitIngress) => (
					<IngressItem
						key={ingress.id}
						ingress={ingress}
						onDelete={handleDelete}
					/>
				))}
			</div>
		</div>
	);
}
